<template>
  <div class="page-layout" data-track="Analytics - Net Promoter Score Landing">
    <!-- Header Section Begins -->
    <div class="level page-info-section">
      <div class="level-left page-layout-head">
        Net promoter score
      </div>
      <div class="level-right">
        <date-select-filter :filter_items="filters.dateFilter.props" :styleProp="filters.dateFilter.styleProp"
                            class="custom-dropdown custom-dropdown--small padding-top-0 padding-right-0" data-track="Date change - Net Promoter Score analytics"
                            @onDateChange="handleDateChange"/>
        <export-reviews :bizSlug="businessSlug" :disabled="!isPaidUser" :endDate="filters.dateFilter.endDate"
                        :rating="filters.starRating" :startDate="filters.dateFilter.startDate"/>
        <!--        <a :disabled="!isPaidUser" :href="exportReviews" class="button font-size-11"
                   data-track="Export - Net Promoter Score analytics">EXPORT</a>-->
      </div>
    </div>
    <hr class="horizontal-row">
    <!-- Header Section Ends -->

    <div class="pad-left-11">
      <template v-if="isPaidUser">
        <div>
          <div class="is-clearfix chart-box">
            <!-- Star Filter Beg you guys available for the schedins -->
            <star-rating-filter
              class="rating-dropdown custom-dropdown"
              data-track="Star rating - Net Promoter Score analytics"
              @onRatingFilterChange="handleStarRatingFilter"
            >
            </star-rating-filter>
            <!-- Star Filter Ends -->

            <div class="chart-box__title">
              Net Promoter Score
              <tooltip-new class="margin-left-10" content-width="350px">
                This chart displays your NPS score based on the number of NPS responses received within the selected
                date range
              </tooltip-new>
            </div>

            <div v-if="canFetchData" class="has-spinner">
              <img v-if="isLoading" class="has-spinner__spinner" src="/static/img/spinner.svg">
              <vue-chart
                :key="keyIdx"
                :columns="chartColumns"
                :options="chartOptions"
                :rows="chartRows"
                chart-type="LineChart"
              >
              </vue-chart>
            </div>
            <div v-else class="noGraph">
              Please select a star rating from the option above
            </div>
          </div>

          <div class="is-flex">
            <div class="simple-box nps-score-box">
              <div class="level margin-bottom-0">
                <div class="level-left">
                  <img class="margin-right-15" src="/static/img/analytics/clipboard.svg">
                  Your NPS score
                </div>
                <div class="level-right font-size-24 has-text-weight-bold" v-text="nps.score"></div>
              </div>
              <hr class="simple-box__head-hr"/>
              <div class="has-text-centered">
                <div>What your score means</div>
                <div class="nps-score-box__text">
                  <template v-if="nps.score < 0">
                    <div class="has-text-weight-bold">
                      Poor customer loyalty.
                    </div>
                    <div>Customers are unlikely to buy from you again</div>
                  </template>
                  <template v-if="nps.score >=0 && nps.score <=49">
                    <div class="has-text-weight-bold">
                      Good customer loyalty.
                    </div>
                    <div>Customers are satisfied with your service</div>
                  </template>
                  <template v-if="nps.score >=50 && nps.score <=69">
                    <div class="has-text-weight-bold">
                      Excellent customer loyalty.
                    </div>
                    <div>Customers are enthusiastic with your service</div>
                  </template>
                  <template v-if="nps.score >=70">
                    <div class="has-text-weight-bold">
                      World class customer loyalty.
                    </div>
                    <div>Customers love you, and your business is getting a lot of positive word-of-mouth from
                      referrals
                    </div>
                  </template>
                </div>
                <div>Understand how your <a class="blue-link"
                                            href="https://intercom.help/hellopeter/en/articles/3677918-your-net-promoter-score"
                                            target="_blank">NPS
                  score</a> is calculated
                </div>
              </div>
            </div>
            <div class="simple-box breakdown-box is-flex has-text-centered">
              <div>
                <img class="breakdown-box__smiley" src="/static/img/reviews/detractors.svg">
                <div :style="{'--barHeight':`${barHeight.detractors}px`}"
                     class="breakdown-box__bar breakdown-box__bar--red"></div>
                <div>
                  <div class="font-size-24 has-text-weight-bold line-height-32" v-text="nps.detractors"></div>
                  Detractors (0 - 6)
                </div>
              </div>
              <div>
                <img class="breakdown-box__smiley" src="/static/img/reviews/passives.svg">
                <div :style="{'--barHeight':`${barHeight.passives}px`}"
                     class="breakdown-box__bar breakdown-box__bar--orange"></div>
                <div>
                  <div class="font-size-24 has-text-weight-bold line-height-32" v-text="nps.passives"></div>
                  Passives (7 - 8)
                </div>
              </div>
              <div>
                <img class="breakdown-box__smiley" src="/static/img/reviews/promoters.svg">
                <div :style="{'--barHeight':`${barHeight.promoters}px`}"
                     class="breakdown-box__bar breakdown-box__bar--green"></div>
                <div>
                  <div class="font-size-24 has-text-weight-bold line-height-32" v-text="nps.promoters"></div>
                  Promoters (9-10)
                </div>
              </div>
            </div>
          </div>

          <div class="simple-box nps-rate-box">
            <div class="level margin-bottom-5 font-size-24 has-text-weight-bold">
              <div class="level-left">
                {{ reviews.haveNps }} / {{ reviews.total }}
                <tooltip-new class="margin-left-10" content-width="350px">
                  The number of reviews for the selected date range where there is a corresponding NPS response
                </tooltip-new>
              </div>
              <div class="level-right">
                {{ responseRate }}%
              </div>
            </div>
            <progress :value="responseRate" class="progress custom-progress custom-progress--blue margin-bottom-10"
                      max="100"></progress>
            <div class="level">
              <div class="level-left">
                Reviews with NPS response
              </div>
              <div class="level-right">
                NPS response rate
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="upgrade-box">
          <div class="upgrade-box__header">
            Upgrade
            <img align="right" src="/static/img/competitors/lock.svg"/>
          </div>
          <div class="upgrade-box__main">
            Your Net Promoter Score (NPS) is a tool used to measure your customer loyalty. <a
            class="blue-link" href="/billing/edit-plan/g1/monthly">Upgrade</a> your Plan to gain
            access to your Net promoter score.
          </div>
        </div>
        <img class="margin-top-15" src="/static/img/analytics/nps-showcase.png"/>
      </template>
    </div>
  </div>
</template>

<script>
import { DateSelectFilter, StarRatingFilter, ExportReviews } from '@/components'
import { misc, charts } from '@/mixins'
import { TooltipNew } from '@/widgets'
import axios from 'axios'

export default {
  name: 'NPSAnalytics',
  metaInfo: {
    title: 'Business Portal | Hellopeter - NPS Analytics'
  },
  components: {
    DateSelectFilter,
    StarRatingFilter,
    TooltipNew,
    ExportReviews
  },
  mixins: [misc, charts],
  data () {
    return {
      isLoading: false,
      chartColumns: [
        {
          type: 'string',
          label: 'Date'
        },
        {
          type: 'number',
          label: 'NPS'
        }
      ],
      chartRows: [],
      filters: {
        starRating: null,
        dateFilter: {
          label: 'last_6_months',
          endDate: null,
          startDate: null,
          styleProp: {},
          props: [{
            name: 'date',
            label: 'Date',
            showApplyBtn: true,
            default: { type: 'months', value: 6, selected: 'Last 6 months' }, // moment.js placeholder in type
            disabled: [],
            elements: ['Last 7 days', 'Previous month', 'Last 6 months', 'Last 12 months', 'Custom']
          }]
        }
      },
      nps: {
        score: 0,
        detractors: 0,
        passives: 0,
        promoters: 0
      },
      barHeight: {
        set: [10, 18, 26],
        detractors: 0,
        passives: 0,
        promoters: 0
      },
      reviews: {
        total: 0,
        haveNps: 0
      }
    }
  },
  computed: {
    /**
     * Creating a payload for HTTP request
     * @return {object}
     */
    payload () {
      if (this.isPaidUser) {
        return {
          endDate: this.filters.dateFilter.endDate,
          startDate: this.filters.dateFilter.startDate,
          starRating: this.filters.starRating,
          businessSlug: this.businessSlug
        }
      }
      return []
    },
    canFetchData () {
      return this.isPaidUser && this.payload.endDate && (!Array.isArray(this.payload.starRating) || this.payload.starRating.length !== 1 || this.payload.starRating[0] !== null)
    },
    responseRate () {
      if (this.reviews.total) {
        return parseFloat(((this.reviews.haveNps / this.reviews.total) * 100).toFixed(1))
      }
      return 0
    }
  },
  methods: {
    /**
     * Handle Date Dropdown Filter
     * @param {object} dateFilter Date data to sort on
     * @return {void}
     */
    handleDateChange (dateFilter) {
      Object.assign(this.filters.dateFilter, dateFilter)
    },
    /**
     * Handle Star Rating Dropdown Filter
     * @param {array} ratingFilter Star Rating to Sort On
     * @return {void}
     */
    handleStarRatingFilter (ratingFilter) {
      this.filters.starRating = ratingFilter
    },
    /**
     * Fetching the review analytics stats
     * @return {void}
     */
    fetchAnalyticsStats () {
      if (this.canFetchData) {
        this.isLoading = true
        this.chartRows = []

        const payload = JSON.parse(JSON.stringify(this.payload))
        axios.get(`/analytics/${payload.businessSlug}/nps`, {
          params: payload
        })
          .then(({ data }) => {
            this.chartRows = data.data.breakdown
            this.nps.score = data.data.npsData.nps_total
            this.nps.detractors = parseInt(data.data.npsData.detractors, 10)
            this.nps.passives = parseInt(data.data.npsData.passives, 10)
            this.nps.promoters = parseInt(data.data.npsData.promoters, 10)

            this.barHeight.detractors = this.calcHeight(this.nps.detractors, [this.nps.passives, this.nps.promoters])
            this.barHeight.passives = this.calcHeight(this.nps.passives, [this.nps.detractors, this.nps.promoters])
            this.barHeight.promoters = this.calcHeight(this.nps.promoters, [this.nps.detractors, this.nps.passives])

            this.reviews.total = data.data.reviews.total
            this.reviews.haveNps = data.data.reviews.haveNps

            this.isLoading = false
            this.keyIdx++
          })
      }
    },
    calcHeight (targetVal, otherVal) {
      if (otherVal.includes(targetVal)) {
        return this.barHeight.set[1]
      } else {
        otherVal.push(targetVal)
        otherVal.sort((a, b) => a - b)
        return this.barHeight.set[otherVal.indexOf(targetVal)]
      }
    }
  },
  watch: {
    payload (newVal, oldVal) {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        this.fetchAnalyticsStats()
      }
    }
  }
}
</script>

<style scoped>
.page-layout {
  margin-left: -16px;
  margin-right: -5px;
}

.page-layout .page-layout-head {
  font-size: 12px;
  margin-left: 11px;
  color: #58595b;
}

.level .level-right a:not(.export-btn) {
  width: 130px;
  height: 35px;
  color: #58595b;
  border-radius: 2px;
  border: solid 1px #58595b;
}

.horizontal-row {
  margin-top: 0;
  margin-bottom: 1rem;
  margin-left: -5%;
  margin-right: -5%;
}

.page-info-section {
  position: relative;
  margin-bottom: 10px;
}

.page-info-section .dropdown-trigger button {
  padding-left: 20px !important;
}

.upgrade-box__header {
  padding: 9px 25px 8px
}

.upgrade-box__main {
  padding-bottom: 35px
}

.has-spinner {
  margin-top: 13px;
}

.nps-score-box {
  width: 390px;
  margin-right: 15px;
  padding: 9px 15px 14px;
}

.nps-score-box__text {
  margin: 16px 0;
}

.breakdown-box {
  width: 560px;
  padding: 25px 45px;
}

.breakdown-box__smiley {
  width: 39px;
}

.breakdown-box__bar {
  height: 26px;
  position: relative;
  width: 156px;
  margin: 5px 0 10px;
}

.breakdown-box__bar--red {
  background: linear-gradient(0deg, #ef5546 var(--barHeight), #fff 0, #fff 100%);
}

.breakdown-box__bar--orange {
  background: linear-gradient(0deg, #fbbb4f var(--barHeight), #fff 0, #fff 100%);
}

.breakdown-box__bar--green {
  background: linear-gradient(0deg, #24b889 var(--barHeight), #fff 0, #fff 100%);
}

.nps-rate-box {
  width: 390px;
  margin-top: 15px;
  padding: 15px 20px;
}
</style>



// WEBPACK FOOTER //
// src/pages/analytics/nps/NPSAnalytics.vue